import { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ja, enUS } from 'date-fns/locale';

interface CustomDatePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  excludeDates?: Date[];
  placeholderText?: string;
  disabled?: boolean;
  locale?: string;
}

const CustomDatePicker = forwardRef<HTMLInputElement, CustomDatePickerProps>(
  ({ selected, onChange, minDate, excludeDates = [], placeholderText, disabled, locale = 'ja' }, ref) => {
    const localeMap = {
      ja: ja,
      en: enUS,
    };

    // 日付を正規化（時刻を00:00:00に設定）
    const normalizeDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // 除外日付のセットを作成（高速検索用）
    const excludedDateSet = new Set(
      excludeDates.map(date => normalizeDate(date))
    );

    console.log('📅 CustomDatePicker - 除外日付:', {
      count: excludeDates.length,
      dates: Array.from(excludedDateSet)
    });

    // 日付が予約できない日かどうかを判定
    const isExcludedDate = (date: Date): boolean => {
      const dateStr = normalizeDate(date);
      const isExcluded = excludedDateSet.has(dateStr);
      
      if (isExcluded) {
        console.log(`❌ ブロック日検出: ${dateStr}`);
      }
      
      return isExcluded;
    };

    // 日付が過去の日付かどうかを判定
    const isPastDate = (date: Date): boolean => {
      if (!minDate) return false;
      const dateStr = normalizeDate(date);
      const minDateStr = normalizeDate(minDate);
      return dateStr < minDateStr;
    };

    // 各日付にカスタムクラスを付与
    const dayClassName = (date: Date): string => {
      const classes: string[] = [];
      
      if (isExcludedDate(date)) {
        classes.push('blocked-date-cell');
        console.log(`🎨 クラス付与: ${normalizeDate(date)} → blocked-date-cell`);
      } else if (isPastDate(date)) {
        classes.push('past-date-cell');
      } else {
        classes.push('available-date-cell');
      }
      
      return classes.join(' ');
    };

    // 各日付の表示内容をカスタマイズ
    const renderDayContents = (day: number, date: Date): JSX.Element => {
      if (isExcludedDate(date)) {
        return (
          <div className="date-content blocked-content">
            <span className="date-number">{day}</span>
            <span className="blocked-mark">×</span>
          </div>
        );
      }
      return <div className="date-content">{day}</div>;
    };

    return (
      <>
        <DatePicker
          ref={ref}
          selected={selected}
          onChange={onChange}
          minDate={minDate}
          excludeDates={excludeDates}
          placeholderText={placeholderText}
          disabled={disabled}
          locale={localeMap[locale as keyof typeof localeMap]}
          dateFormat="yyyy/MM/dd"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
          calendarClassName="custom-calendar"
          dayClassName={dayClassName}
          renderDayContents={renderDayContents}
          inline={false}
        />
        <style>{`
          /* 予約可能日 - 白背景 */
          .react-datepicker__day.available-date-cell {
            background-color: #ffffff !important;
            color: #1f2937 !important;
          }
          
          .react-datepicker__day.available-date-cell:hover {
            background-color: #f0fdfa !important;
          }

          /* 予約できない日 - 薄いピンク背景 + 赤い×マーク */
          .react-datepicker__day.blocked-date-cell {
            background-color: #fecaca !important;
            color: #991b1b !important;
            cursor: not-allowed !important;
            position: relative !important;
            pointer-events: none !important;
          }

          .react-datepicker__day.blocked-date-cell:hover {
            background-color: #fca5a5 !important;
          }

          /* 過去の日付 - グレー背景 */
          .react-datepicker__day.past-date-cell {
            background-color: #f3f4f6 !important;
            color: #9ca3af !important;
            cursor: not-allowed !important;
          }

          /* 日付コンテンツのスタイル */
          .date-content {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            position: relative;
          }

          /* 予約できない日の×マーク */
          .blocked-content {
            flex-direction: column;
            gap: 2px;
          }

          .blocked-content .date-number {
            font-size: 14px;
            line-height: 1;
          }

          .blocked-content .blocked-mark {
            font-size: 16px;
            font-weight: bold;
            color: #dc2626 !important;
            line-height: 1;
          }

          /* カレンダー全体のスタイル調整 */
          .custom-calendar .react-datepicker__day {
            width: 2.5rem !important;
            height: 2.5rem !important;
            line-height: 2.5rem !important;
            margin: 0.2rem !important;
            border-radius: 0.375rem !important;
          }

          /* 選択された日付 */
          .react-datepicker__day--selected {
            background-color: #14b8a6 !important;
            color: white !important;
          }

          .react-datepicker__day--selected:hover {
            background-color: #0d9488 !important;
          }

          /* 今日の日付 */
          .react-datepicker__day--today {
            font-weight: bold !important;
            border: 2px solid #14b8a6 !important;
          }

          /* カレンダーヘッダー */
          .react-datepicker__header {
            background-color: #f0fdfa !important;
            border-bottom: 1px solid #99f6e4 !important;
          }

          .react-datepicker__current-month {
            color: #0f766e !important;
            font-weight: 600 !important;
          }

          .react-datepicker__day-name {
            color: #0f766e !important;
            font-weight: 500 !important;
          }

          /* ナビゲーションボタン */
          .react-datepicker__navigation {
            top: 10px !important;
          }

          .react-datepicker__navigation-icon::before {
            border-color: #0f766e !important;
          }

          .react-datepicker__navigation:hover *::before {
            border-color: #14b8a6 !important;
          }
        `}</style>
      </>
    );
  }
);

CustomDatePicker.displayName = 'CustomDatePicker';

export default CustomDatePicker;
